import { useMemo } from 'react';
import { useI18n } from '../i18n/I18nProvider';

function safeText(s) {
  return String(s || '').trim();
}

function initialFromName(name) {
  const t = safeText(name);
  if (!t) return 'B';
  return t.slice(0, 1).toUpperCase();
}

function normalizeUsername(name) {
  const n = safeText(name);
  if (!n) return 'brand';
  return (
    n
      .toLowerCase()
      .replace(/[^\p{L}\p{N}_ ]/gu, '')
      .trim()
      .replace(/\s+/g, '_')
      .slice(0, 24) || 'brand'
  );
}

export default function InstagramBrandKitLivePreview({
  brandName,
  profileName,
  bio,
  avatarDataUrl,
  highlights,
  gridItems,
  size = 'md', // md | xl
  onPickAvatar,
  onPickProfileName,
  onPickBio,
  onPickHighlight,
  onPickGrid,
}) {
  const { t } = useI18n();
  const username = useMemo(() => normalizeUsername(brandName), [brandName]);
  const displayName = safeText(profileName) || safeText(brandName) || username;
  const bioText = safeText(bio) || t('brandKit.pack.liveHint');
  const grid = Array.isArray(gridItems) ? gridItems : [];
  const hl = Array.isArray(highlights) ? highlights : [];

  const sizeCls = size === 'xl' ? 'IGMock--xl' : 'IGMock--md';

  return (
    <div className={`IGMock IGMock--profile IGMock--editable ${sizeCls}`}>
      <div className="IGProfileTop">
        <div className="IGProfileTopLeft">
          <div className="IGUsername">{username}</div>
        </div>
        <div className="IGProfileTopRight" aria-hidden="true">
          <span className="IGDots">•••</span>
        </div>
      </div>

      <div className="IGProfileHeader">
        <button type="button" className="IGTap IGAvatarTap" onClick={onPickAvatar} aria-label={t('brandKit.pack.livePickAvatar')}>
          <div className="IGAvatar IGAvatar--lg">
            {avatarDataUrl ? <img className="IGAvatarImg" src={avatarDataUrl} alt="" /> : <span className="IGAvatarFallback">{initialFromName(username)}</span>}
          </div>
          <div className="IGEditDot" aria-hidden="true" />
        </button>

        <div className="IGProfileStats" aria-hidden="true">
          <div className="IGStat">
            <div className="IGStatNum">12</div>
            <div className="IGStatLabel">posts</div>
          </div>
          <div className="IGStat">
            <div className="IGStatNum">2.3k</div>
            <div className="IGStatLabel">followers</div>
          </div>
          <div className="IGStat">
            <div className="IGStatNum">143</div>
            <div className="IGStatLabel">following</div>
          </div>
        </div>
      </div>

      <div className="IGProfileBio">
        <button type="button" className="IGTap IGProfileNameTap" onClick={onPickProfileName} aria-label={t('brandKit.pack.livePickProfileName')}>
          <div className="IGProfileName">{displayName}</div>
          <div className="IGEditDot" aria-hidden="true" />
        </button>
        <button type="button" className="IGTap IGProfileBioTap" onClick={onPickBio} aria-label={t('brandKit.pack.livePickBio')}>
          <div className="IGProfileBioText IGCaption--dim">{bioText}</div>
          <div className="IGEditDot" aria-hidden="true" />
        </button>
      </div>

      <div className="IGHighlights" aria-hidden="false">
        {hl.length ? (
          hl.map((h, idx) => (
            <button
              key={h.id || `${idx}`}
              type="button"
              className="IGTap IGHl"
              onClick={() => onPickHighlight?.(idx)}
              aria-label={t('brandKit.pack.livePickHighlight', { label: h.label || '' })}
            >
              <div
                className={`IGHlCircle ${h.dataUrl ? 'IGHlCircle--img' : ''}`}
                style={h.dataUrl ? { backgroundImage: `url(${h.dataUrl})` } : undefined}
              />
              <div className="IGHlLabel">{h.label || '—'}</div>
            </button>
          ))
        ) : (
          <div className="IGCaption IGCaption--dim" style={{ padding: '6px 0' }}>
            {t('brandKit.pack.liveNoHighlights')}
          </div>
        )}
      </div>

      <div className="IGProfileTabs" aria-hidden="true">
        <span className="IGTab IGTab--active">▦</span>
        <span className="IGTab">▶</span>
        <span className="IGTab">▢</span>
      </div>

      <div className="IGGrid">
        {Array.from({ length: 9 }).map((_, idx) => {
          const url = safeText(grid[idx]);
          return (
            <button
              key={idx}
              type="button"
              className={`IGTap IGGridItem ${url ? '' : 'IGGridItem--ph'} `}
              onClick={() => onPickGrid?.(idx)}
              aria-label={t('brandKit.pack.livePickGrid', { index: idx + 1 })}
            >
              {url ? <img className="IGGridMedia" src={url} alt="" /> : <span className="IGGridPlus" aria-hidden="true">+</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
